import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  Heading,
  Text,
  IconButton,
  Flex,
  Grid,
  Card,
  CardHeader,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Checkbox,
  Spinner,
  Image,
  FormHelperText,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { ArrowBackIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons';
import { IoSave } from 'react-icons/io5';
import AuthContext from '../context/AuthContext';
import { characterService } from '../services/api';
import { useAppToast } from '../utils/toast';

// Array field component for dynamic lists
const ArrayField = ({ label, name, defaultValues = [''], helperText, bgColor }) => {
  const initialValues = Array.isArray(defaultValues) && defaultValues.length > 0 ? defaultValues : [''];
  const [fields, setFields] = useState(initialValues);
  
  const addField = () => setFields([...fields, '']);
  
  const removeField = (index) => {
    if (fields.length > 1) {
      setFields(fields.filter((_, i) => i !== index));
    }
  };
  
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      {helperText && <FormHelperText mb={2}>{helperText}</FormHelperText>}
      <VStack align="stretch" spacing={2}>
        {fields.map((value, i) => (
          <HStack key={i}>
            <Input 
              name={name}
              defaultValue={value}
              bg={bgColor}
              placeholder={`Enter ${label.toLowerCase()}`}
            />
            {fields.length > 1 && (
              <IconButton
                icon={<DeleteIcon />}
                aria-label="Remove"
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={() => removeField(i)}
              />
            )}
          </HStack>
        ))}
        <Button
          leftIcon={<AddIcon />}
          size="sm"
          variant="outline"
          onClick={addField}
          alignSelf="flex-start"
        >
          Add {label}
        </Button>
      </VStack>
    </FormControl>
  );
};

// Textarea array field component for dynamic lists
const TextareaArrayField = ({ label, name, defaultValues = [''], rows = 3, helperText, bgColor }) => {
  const initialValues = Array.isArray(defaultValues) && defaultValues.length > 0 ? defaultValues : [''];
  const [fields, setFields] = useState(initialValues);
  
  const addField = () => setFields([...fields, '']);
  
  const removeField = (index) => {
    if (fields.length > 1) {
      setFields(fields.filter((_, i) => i !== index));
    }
  };
  
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      {helperText && <FormHelperText mb={2}>{helperText}</FormHelperText>}
      <VStack align="stretch" spacing={2}>
        {fields.map((value, i) => (
          <HStack key={i} align="flex-start">
            <Textarea 
              name={name}
              defaultValue={value}
              rows={rows}
              bg={bgColor}
              placeholder={`Enter ${label.toLowerCase()}`}
            />
            {fields.length > 1 && (
              <IconButton
                icon={<DeleteIcon />}
                aria-label="Remove"
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={() => removeField(i)}
              />
            )}
          </HStack>
        ))}
        <Button
          leftIcon={<AddIcon />}
          size="sm"
          variant="outline"
          onClick={addField}
          alignSelf="flex-start"
        >
          Add {label}
        </Button>
      </VStack>
    </FormControl>
  );
};

function CharacterEdit() {
  const { token } = useContext(AuthContext);
  const { showErrorToast, showSuccessToast } = useAppToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [character, setCharacter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [image, setImage] = useState(null);
  const formRef = useRef(null);
  
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const accordionExpandedBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    fetchCharacter();
  }, [id]);

  const fetchCharacter = async () => {
    try {
      const data = await characterService.getCharacterById(id);
      setCharacter(data);
      setIsLoading(false);
    } catch (error) {
      showErrorToast('Error fetching character', error.message);
      navigate('/');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    const formData = new FormData(e.target);
    const updatedProfile = {};

    // Simplified nested object builder
    const buildNestedObject = (obj, path, value) => {
      const keys = path.split('.');
      const lastKey = keys.pop();
      
      const target = keys.reduce((acc, key) => {
        if (!acc[key]) acc[key] = {};
        return acc[key];
      }, obj);
      
      target[lastKey] = value;
    };

    // Process form entries
    const entries = {};
    for (let [key, value] of formData.entries()) {
      if (!entries[key]) entries[key] = [];
      entries[key].push(value);
    }

    // Build nested structure
    for (let [key, values] of Object.entries(entries)) {
      const filteredValues = values.filter(v => v);
      if (filteredValues.length === 0) continue;
      
      const value = filteredValues.length > 1 ? filteredValues : filteredValues[0];
      
      // Auto-convert numeric fields
      const numericFields = ['age', 'height', 'weight', 'cock_length', 'cock_girth', 'height_cm', 'weight_kg'];
      const finalValue = typeof value === 'string' && numericFields.some(field => key.includes(field)) 
        ? parseFloat(value) || 0 
        : value;
      
      buildNestedObject(updatedProfile, key, finalValue);
    }

    // Handle conversions
    if (updatedProfile.core_identity?.height_feet || updatedProfile.core_identity?.height_inches) {
      const feet = parseFloat(updatedProfile.core_identity.height_feet) || 0;
      const inches = parseFloat(updatedProfile.core_identity.height_inches) || 0;
      updatedProfile.core_identity.height_cm = (feet * 12 + inches) * 2.54;
      delete updatedProfile.core_identity.height_feet;
      delete updatedProfile.core_identity.height_inches;
    }

    if (updatedProfile.core_identity?.weight_lbs) {
      updatedProfile.core_identity.weight_kg = parseFloat(updatedProfile.core_identity.weight_lbs) / 2.20462;
      delete updatedProfile.core_identity.weight_lbs;
    }

    // Age gate: the edited profile must describe an adult (18+).
    const editedAge = parseInt(updatedProfile?.core_identity?.age, 10);
    if (!Number.isFinite(editedAge) || editedAge < 18) {
      showErrorToast('Invalid age', 'Character age must be 18 or older.');
      setIsSaving(false);
      return;
    }

    try {
      const updateData = new FormData();
      updateData.append('character_profile', JSON.stringify(updatedProfile));
      
      // Handle checkbox - unchecked doesn't appear in FormData
      const isPublic = formData.get('isPublic') !== null;
      updateData.append('isPublic', isPublic ? 'true' : 'false');
      
      if (image) {
        updateData.append('image', image);
      }

      await characterService.updateCharacter(id, updateData);

      showSuccessToast('Character updated successfully!');
      navigate(`/view-character/${id}`);
    } catch (error) {
      showErrorToast('Error saving character', error.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="70vh">
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (!character) return null;

  const profile = character.character_profile;
  
  // Calculate derived values after all hooks and conditional returns
  const heightCm = profile.core_identity?.height_cm || 0;
  const heightInches = heightCm / 2.54;
  const heightFeet = Math.floor(heightInches / 12);
  const heightRemainderInches = (heightInches % 12).toFixed(1);
  const weightLbs = ((profile.core_identity?.weight_kg || 0) * 2.20462).toFixed(1);

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Container maxW="container.xl">
        <HStack mb={6} spacing={4}>
          <Button
            leftIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            variant="outline"
          >
            Back
          </Button>
          <Button
            leftIcon={<IoSave />}
            colorScheme="brand"
            type="submit"
            form="character-form"
            isLoading={isSaving}
            loadingText="Saving..."
          >
            Save Changes
          </Button>
        </HStack>

        <form id="character-form" ref={formRef} onSubmit={handleSubmit}>
          <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6} mb={6}>
            <Card bg={cardBg}>
              <CardHeader>
                <Heading size="lg">Edit Character: {profile.character_name}</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Character Name</FormLabel>
                    <Input 
                      name="character_name" 
                      defaultValue={profile.character_name} 
                      required 
                      bg={inputBg}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Character Image</FormLabel>
                    <Input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => setImage(e.target.files[0])}
                      p={1}
                    />
                  </FormControl>

                  <Checkbox 
                    name="isPublic" 
                    defaultChecked={character.isPublic}
                    value="true"
                  >
                    Make this character public
                  </Checkbox>
                </VStack>
              </CardBody>
            </Card>

            {character.imageData && (
              <Card bg={cardBg}>
                <CardHeader>
                  <Heading size="md">Current Image</Heading>
                </CardHeader>
                <CardBody>
                  <Image 
                    src={`/api/characters/${id}/image`} 
                    alt={profile.character_name}
                    borderRadius="lg"
                    objectFit="cover"
                    width="100%"
                  />
                </CardBody>
              </Card>
            )}
          </Grid>

          <Accordion allowMultiple defaultIndex={[0]}>
            <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
              <h2>
                <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md">Core Identity</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Legal Name</FormLabel>
                    <Input name="core_identity.names.legal" defaultValue={profile.core_identity?.names?.legal} bg={inputBg} />
                  </FormControl>

                  <ArrayField 
                    label="Nicknames"
                    name="core_identity.names.nicknames"
                    defaultValues={profile.core_identity?.names?.nicknames || []}
                    bgColor={inputBg}
                  />

                  <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                    <FormControl>
                      <FormLabel>Race</FormLabel>
                      <Input name="core_identity.race" defaultValue={profile.core_identity?.race} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Species</FormLabel>
                      <Input name="core_identity.species" defaultValue={profile.core_identity?.species} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Gender</FormLabel>
                      <Select name="core_identity.gender" defaultValue={profile.core_identity?.gender} bg={inputBg}>
                        <option value="">Choose...</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
                    <FormControl>
                      <FormLabel>Age</FormLabel>
                      <Input type="number" name="core_identity.age" min={18} defaultValue={profile.core_identity?.age} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Height (feet)</FormLabel>
                      <Input type="number" step="1" name="core_identity.height_feet" defaultValue={heightFeet} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Height (inches)</FormLabel>
                      <Input type="number" step="0.1" name="core_identity.height_inches" defaultValue={heightRemainderInches} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Weight (lbs)</FormLabel>
                      <Input type="number" step="0.1" name="core_identity.weight_lbs" defaultValue={weightLbs} bg={inputBg} />
                    </FormControl>
                  </Grid>

                  <ArrayField 
                    label="Build Descriptors"
                    name="core_identity.build.descriptors"
                    defaultValues={profile.core_identity?.build?.descriptors || []}
                    helperText="Physical build characteristics (e.g., muscular, slim, athletic)"
                    bgColor={inputBg}
                  />
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
              <h2>
                <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md">Physical Appearance</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Face Description</FormLabel>
                    <Textarea name="core_identity.face.description" defaultValue={profile.core_identity?.face?.description} rows={4} bg={inputBg} />
                  </FormControl>

                  <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                    <FormControl>
                      <FormLabel>Hair Color</FormLabel>
                      <Input name="core_identity.face.hair_color" defaultValue={profile.core_identity?.face?.hair_color} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Eye Color</FormLabel>
                      <Input name="core_identity.face.eye_color" defaultValue={profile.core_identity?.face?.eye_color} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Face Skin Tone</FormLabel>
                      <Input name="core_identity.face.skin_tone" defaultValue={profile.core_identity?.face?.skin_tone} bg={inputBg} />
                    </FormControl>
                  </Grid>

                  <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                    <FormControl>
                      <FormLabel>Body Skin Tone</FormLabel>
                      <Input name="core_identity.body.skin_tone" defaultValue={profile.core_identity?.body?.skin_tone} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Musculature</FormLabel>
                      <Input name="core_identity.body.musculature" defaultValue={profile.core_identity?.body?.musculature} bg={inputBg} />
                    </FormControl>
                  </Grid>

                  <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                    <ArrayField 
                      label="Injuries"
                      name="core_identity.body.injuries"
                      defaultValues={profile.core_identity?.body?.injuries || []}
                      bgColor={inputBg}
                    />
                    <ArrayField 
                      label="Tattoos"
                      name="core_identity.body.tattoos"
                      defaultValues={profile.core_identity?.body?.tattoos || []}
                      bgColor={inputBg}
                    />
                    <ArrayField 
                      label="Piercings"
                      name="core_identity.body.piercings"
                      defaultValues={profile.core_identity?.body?.piercings || []}
                      bgColor={inputBg}
                    />
                  </Grid>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
              <h2>
                <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md">Sexual Details</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <VStack spacing={4} align="stretch">
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                    <FormControl>
                      <FormLabel>Orientation</FormLabel>
                      <Input name="core_identity.sexual_details.orientation" defaultValue={profile.core_identity?.sexual_details?.orientation} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Position</FormLabel>
                      <Input name="core_identity.sexual_details.position" defaultValue={profile.core_identity?.sexual_details?.position} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Cock Length (inches)</FormLabel>
                      <Input type="number" step="0.1" name="core_identity.sexual_details.cock_length_inches.value" defaultValue={profile.core_identity?.sexual_details?.cock_length_inches?.value} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Cock Circumference (inches)</FormLabel>
                      <Input type="number" step="0.1" name="core_identity.sexual_details.cock_circumference_inches.value" defaultValue={profile.core_identity?.sexual_details?.cock_circumference_inches?.value} bg={inputBg} />
                    </FormControl>
                  </Grid>
                  <FormControl>
                    <FormLabel>Experience Level</FormLabel>
                    <Textarea name="core_identity.sexual_details.experience_level" defaultValue={profile.core_identity?.sexual_details?.experience_level} rows={3} bg={inputBg} />
                  </FormControl>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
              <h2>
                <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md">Backstory & Motivation</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <VStack spacing={4} align="stretch">
                  <TextareaArrayField 
                    label="Major Events"
                    name="backstory_and_motivation.major_events"
                    defaultValues={profile.backstory_and_motivation?.major_events || []}
                    rows={3}
                    helperText="Significant events that shaped the character"
                    bgColor={inputBg}
                  />
                  <FormControl>
                    <FormLabel>Primary Motivation</FormLabel>
                    <Textarea 
                      name="backstory_and_motivation.primary_motivation" 
                      defaultValue={profile.backstory_and_motivation?.primary_motivation} 
                      rows={3} 
                      bg={inputBg}
                      placeholder="What drives this character?"
                    />
                  </FormControl>
                  <ArrayField 
                    label="Current Motivations"
                    name="backstory_and_motivation.current_motivations"
                    defaultValues={profile.backstory_and_motivation?.current_motivations || []}
                    helperText="What the character currently wants or needs"
                    bgColor={inputBg}
                  />
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
              <h2>
                <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md">Personality & Voice</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <VStack spacing={4} align="stretch">
                  <ArrayField 
                    label="Surface Personality"
                    name="personality_and_voice.surface_personality"
                    defaultValues={profile.personality_and_voice?.surface_personality || []}
                    helperText="How they appear to others"
                  />

                  <ArrayField 
                    label="Core Personality"
                    name="personality_and_voice.core_personality"
                    defaultValues={profile.personality_and_voice?.core_personality || []}
                    helperText="Their true nature"
                  />

                  <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                    <FormControl>
                      <FormLabel>Speech Volume</FormLabel>
                      <Input name="personality_and_voice.speech_characteristics.volume" defaultValue={profile.personality_and_voice?.speech_characteristics?.volume} bg={inputBg} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Speaking Speed</FormLabel>
                      <Input name="personality_and_voice.speech_characteristics.speed" defaultValue={profile.personality_and_voice?.speech_characteristics?.speed} bg={inputBg} />
                    </FormControl>
                  </Grid>

                  <FormControl>
                    <FormLabel>Verbal Tics</FormLabel>
                    <Textarea name="personality_and_voice.speech_characteristics.verbal_tics" defaultValue={profile.personality_and_voice?.speech_characteristics?.verbal_tics} rows={3} bg={inputBg} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Inflections or Tics</FormLabel>
                    <Textarea name="personality_and_voice.speech_characteristics.inflections_or_tics" defaultValue={profile.personality_and_voice?.speech_characteristics?.inflections_or_tics} rows={3} bg={inputBg} />
                  </FormControl>

                  <ArrayField 
                    label="Sexual Relationship Personality"
                    name="personality_and_voice.sexual_relationship_personality"
                    defaultValues={profile.personality_and_voice?.sexual_relationship_personality || []}
                  />
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
              <h2>
                <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md">Abilities & Action Style</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <VStack spacing={4} align="stretch">
                  <ArrayField 
                    label="Abilities"
                    name="abilities_and_action_style.abilities"
                    defaultValues={profile.abilities_and_action_style?.abilities || []}
                    helperText="Skills, powers, or special abilities"
                  />

                  <ArrayField 
                    label="Traits"
                    name="abilities_and_action_style.traits"
                    defaultValues={profile.abilities_and_action_style?.traits || []}
                    helperText="Action style and behavior traits"
                  />
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
              <h2>
                <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md">Style Cheat Sheet</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <VStack spacing={4} align="stretch">
                  <TextareaArrayField 
                    label="Examples"
                    name="style_cheat_sheet.examples"
                    defaultValues={profile.style_cheat_sheet?.examples || []}
                    rows={3}
                    helperText="Example behaviors or dialogue"
                    bgColor={inputBg}
                  />

                  <ArrayField 
                    label="Guidelines"
                    name="style_cheat_sheet.guidelines"
                    defaultValues={profile.style_cheat_sheet?.guidelines || []}
                    helperText="Rules for how this character behaves"
                  />
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem bg={cardBg} borderRadius="lg" mb={4}>
              <h2>
                <AccordionButton _expanded={{ bg: accordionExpandedBg }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md">Erotic Content Policy</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <VStack spacing={4} align="stretch">
                  <ArrayField 
                    label="Allowed Kinks"
                    name="erotic_content_policy.allowed_kinks"
                    defaultValues={profile.erotic_content_policy?.allowed_kinks || []}
                    helperText="Content this character can engage with"
                  />

                  <ArrayField 
                    label="Hard Limits"
                    name="erotic_content_policy.hard_limits.items"
                    defaultValues={profile.erotic_content_policy?.hard_limits?.items || []}
                    helperText="Content this character will never engage with"
                  />
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </form>
      </Container>
    </Box>
  );
}

export default CharacterEdit;